/** Base 36 keeps the fallback key short and alphanumeric, so nothing in it has to be escaped. */
const RADIX = 36;

/** Drops the leading "0." that Math.random().toString() always starts with. */
const RANDOM_START = 2;

/** Enough of each draw that two keys colliding inside one session is not worth worrying about. */
const RANDOM_END = 12;

/**
 * A key identifying one user action, sent along with the request that action makes so that the
 * backend can tell a repeat of that action apart from a new one.
 *
 * Two clicks on the same button, or a retry of a request whose answer never arrived, are the same
 * action and carry the same key: the backend answers the repeat with what the first one did instead
 * of putting the item on the order a second time. Deliberately ordering the same item again is a new
 * action with a new key, and is expected to add to the quantity that is already there.
 *
 * Without the key the two are indistinguishable - the payloads are identical - so the backend would
 * have to guess, and guessing either doubles what a brukare is handed or refuses an order somebody
 * meant to place.
 */
const createIdempotencyKey = (): string => {
  // Read as optional whatever the DOM lib promises: randomUUID is missing from older browsers, and
  // where it does exist it is only present at runtime in a secure context - a plain http dev server
  // is not one.
  const webCrypto = globalThis.crypto as { randomUUID?: () => string } | undefined;
  const randomUUID = webCrypto?.randomUUID;

  if (randomUUID) return randomUUID.call(webCrypto);

  const random = () => Math.random().toString(RADIX).slice(RANDOM_START, RANDOM_END);

  return `${Date.now().toString(RADIX)}-${random()}-${random()}`;
};

export default createIdempotencyKey;
